import type { RunResponse } from "@carbon-operations/contracts";
import { Link } from "react-router-dom";

export const runStates: RunResponse["state"][] = [
  "awaiting_approval", "queued", "running", "succeeded", "failed", "cancel_requested", "canceled", "rejected", "expired",
];

export function stateLabel(value: string) {
  return value.split("_").map((word) =>
    `${word[0]?.toUpperCase() ?? ""}${word.slice(1)}`
  ).join(" ");
}

export function stateTone(
  state: RunResponse["state"],
): "neutral" | "success" | "attention" | "danger" {
  switch (state) {
    case "queued":
    case "canceled":
      return "neutral";
    case "awaiting_approval":
    case "cancel_requested":
      return "attention";
    case "running":
    case "succeeded":
      return "success";
    case "failed":
    case "rejected":
    case "expired":
      return "danger";
  }
}

/** Runs of the selected workspace only; the server lists every workspace the caller belongs to unless filtered. */
export function runsInBoundary(runs: RunResponse[], boundary: { tenantId: string; workspaceId: string }): RunResponse[] {
  return runs.filter((run) => run.tenant_id === boundary.tenantId && run.workspace_id === boundary.workspaceId);
}

// Every value is rendered as React text (escaped). Each row opens the existing run detail.
export function RunListTable({ runs, base }: { runs: RunResponse[]; base: string }) {
  if (runs.length === 0) return <p className="empty-state">No runs match</p>;
  return (
    <div className="table-wrap">
      <table className="stacked-table run-list-table" aria-label="Runs">
        <thead>
          <tr>
            <th>Run</th>
            <th>State</th>
            <th>Risk class</th>
            <th>Policy</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.run_id} className="run-list-row">
              <td data-label="Run">
                <Link className="strong-link" to={`${base}/runs/${encodeURIComponent(run.run_id)}`}>{run.run_id}</Link>
                {run.approval_id && run.state === "awaiting_approval" && <small>Awaiting approval {run.approval_id}</small>}
              </td>
              <td data-label="State">
                <span className={`status ${stateTone(run.state)}`}><i />{stateLabel(run.state)}</span>
              </td>
              <td data-label="Risk class">{run.risk_class}</td>
              <td data-label="Policy">{run.policy_decision ? stateLabel(run.policy_decision) : "—"}</td>
              <td data-label="Created"><time dateTime={run.created_at}>{new Date(run.created_at).toLocaleString()}</time></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
